// Maps a SubmittedAccountOp into the compact ActivityCard row shape used by the
// Home v2 dashboard "Recent activity" panel. Reuses the same balance-change
// extraction the full Activity screen (PublicActivityRow) uses, so the
// kind/amount stay consistent across the two surfaces.
import { formatUnits } from 'ethers'

import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import formatDateTime from '@common/utils/formatDateTime'
import {
  formatBalanceChangeAmount,
  getSummaryBalanceChanges
} from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/helpers'
import { SubmittedAccountOpLike } from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/types'

import { ActivityItem } from './components/ActivityCard'
import { ActivityKind } from './tokens'

const LABEL: Record<ActivityKind, string> = {
  swap: 'Swap',
  send: 'Sent',
  receive: 'Received',
  shield: 'Shielded',
  unshield: 'Unshielded',
  other: 'Transaction'
}

export const accountOpToActivityItem = (op: SubmittedAccountOpLike): ActivityItem | null => {
  const nonFee = getSummaryBalanceChanges(op).filter((c) => c.iconType !== 'gasTank')
  const primary = nonFee[0]
  if (!primary) return null

  const hasPos = nonFee.some((c) => c.balanceChange > 0n)
  const hasNeg = nonFee.some((c) => c.balanceChange < 0n)
  let kind: ActivityKind = 'other'
  if (hasPos && hasNeg) kind = 'swap'
  else if (hasNeg) kind = 'send'
  else if (hasPos) kind = 'receive'

  const outgoing = primary.balanceChange < 0n
  // Strip the leading +/- (ActivityCard prepends its own sign) and append symbol.
  const absAmount = formatBalanceChangeAmount(primary).replace(/^[-+]/, '')
  const amount = `${absAmount}${primary.symbol ? ` ${primary.symbol}` : ''}`

  const usdPrice = primary.priceIn?.find((p) => p.baseCurrency === 'usd')?.price
  let amountUsd: string | undefined
  if (usdPrice) {
    const abs = primary.balanceChange < 0n ? -primary.balanceChange : primary.balanceChange
    amountUsd = formatDecimals(parseFloat(formatUnits(abs, primary.decimals)) * usdPrice, 'value')
  }

  return {
    id: op.id || op.txnId || `${op.chainId}-${op.timestamp}`,
    kind,
    label: LABEL[kind],
    time: op.timestamp ? formatDateTime(op.timestamp) : '',
    amount,
    amountUsd,
    outgoing
  }
}
